import { Field, Group } from '@chakra-ui/react'
import { FormErrors } from 'commons/components/layout/FormErrors'
import { Label } from 'commons/components/layout/Label'
import { Section } from 'commons/components/layout/Section'
import { CountrySelect } from 'commons/components/phone/CountrySelect'
import { PhoneInput } from 'commons/components/phone/PhoneInput'
import { useFieldContext } from 'commons/config/form'
import { CountryISO, CountryName, type Country } from 'commons/constants/countries'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import { findCountryWithPhone, sanitize } from 'commons/utils/phoneHelpers'
import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export const PhoneSection = () => {
	const field = useFieldContext<string>()
	const [phone, setPhone] = useState<string>('')
	const [country, setCountry] = useState<Country>({
		iso: CountryISO.FR,
		name: CountryName.FRANCE,
		phone: '+33',
		flag: '🇫🇷'
	})
	const { t } = useTranslation()

	const handleChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
		const phoneValue = e.target.value
		setPhone(phoneValue)

		// Sanitize to remove non-digits (handles paste with +, tel:, etc.)
		let cleanPhone = sanitize(phoneValue)
		// Remove country code digits if present (e.g., '33' for France)
		const countryCodeDigits = country.phone.replace('+', '')
		if (cleanPhone.startsWith(countryCodeDigits)) {
			cleanPhone = cleanPhone.slice(countryCodeDigits.length)
		}
		// Remove leading 0 if present
		if (cleanPhone.startsWith('0')) {
			cleanPhone = cleanPhone.slice(1)
		}

		field.handleChange(country.phone + cleanPhone)
	}

	const handleChangeCountry = (e: ChangeEvent<HTMLSelectElement>) => {
		const newCountry = findCountryWithPhone(e.target.value)
		setCountry(newCountry)

		// Sanitize to remove non-digits
		let cleanPhone = sanitize(phone)
		// Remove old country code digits if present
		const oldCountryCodeDigits = country.phone.replace('+', '')
		if (cleanPhone.startsWith(oldCountryCodeDigits)) {
			cleanPhone = cleanPhone.slice(oldCountryCodeDigits.length)
		}
		// Remove leading 0 if present
		if (cleanPhone.startsWith('0')) {
			cleanPhone = cleanPhone.slice(1)
		}

		field.handleChange(e.target.value + cleanPhone)
	}

	return (
		<Field.Root
			invalid={
				!field.state.meta.isValid || (field.state.meta.errors && field.state.meta.errors.length > 0)
			}
		>
			<Section
				_focusWithin={{ outline: `2px solid ${BgColors.OPPOSITE_Z3}` }}
				_groupInvalid={{
					outline: `2px solid ${TextColors.STATUS_ERROR}`
				}}
			>
				<Label>{t('phone_form.phone_label')}</Label>
				<Group display='flex'>
					<CountrySelect country={country} onChange={handleChangeCountry} />
					<PhoneInput country={country} onChange={handleChangePhone} />
				</Group>
				<FormErrors errors={field.state.meta.errors} />
			</Section>
		</Field.Root>
	)
}
